import React, { useState } from 'react';
import { Box, Container, Grid, Typography, Link, Button } from '@mui/material';

interface HeroBannerProps {}

const HeroBanner: React.FC<HeroBannerProps> = () => {
  const [heroExpanded, setHeroExpanded] = useState<boolean>(false);

  return (
    <Box sx={{
      mb: 4,
      // Match invitation poster gradient
      background: 'linear-gradient(to bottom right, #4eac53, #30aaec)',
      color: (t) => t.palette.common.white,
      // Full-bleed only on md+ to avoid mobile overflow
      width: { xs: '100%', md: '100vw' },
      position: { md: 'relative' },
      left: { md: '50%' },
      right: { md: '50%' },
      marginLeft: { md: '-50vw' },
      marginRight: { md: '-50vw' },
    }}>
      <Container maxWidth="lg" sx={{ px: { xs: 2, md: 4 }, py: { xs: 4, md: 10 } }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  color: 'inherit',
                  m: 0,
                  fontWeight: 800,
                  fontSize: { xs: (t) => t.typography.h5.fontSize, md: (t) => t.typography.h3.fontSize },
                  lineHeight: 1.15,
                }}
              >
                Productive Capabilities Data Lab
              </Typography>
              {/* Mobile: same paragraph with expandable preview */}
              <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 1.5, textAlign: { xs: 'center', md: 'left' } }}>
                <Box sx={{
                  overflow: 'hidden',
                  transition: 'max-height 200ms ease',
                  ...(heroExpanded ? { maxHeight: 'none' } : { maxHeight: '4.5em', maskImage: 'linear-gradient(to bottom, black 70%, transparent 100%)' })
                }}>
                  <Typography variant="body2" sx={{ color: 'inherit', lineHeight: 1.5, textAlign: 'inherit' }}>
                    The Productive Capabilities Data Lab is an applied AI research and development hub specializing in analytics and datasets for industrial strategy: It is a non-profit, public-purpose collaboration between{' '}
                    <Link
                      href="https://www.i3-t.org/"
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{ color: 'common.white', fontWeight: 700, '&:hover': { color: 'common.white', textDecoration: 'underline' } }}
                    >
                      i3T
                    </Link>{' '}and{' '}
                    <Link
                      href="https://rubidiumdata.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{ color: 'common.white', fontWeight: 700, '&:hover': { color: 'common.white', textDecoration: 'underline' } }}
                    >
                      RubidiumData
                    </Link>.
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                  <Link component="button" variant="body2" onClick={() => setHeroExpanded(!heroExpanded)} sx={{ color: 'common.white', opacity: 0.9 }}>
                    {heroExpanded ? 'Show less' : 'Read more'}
                  </Link>
                </Box>
              </Box>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' }, gap: 2, flexWrap: 'wrap' }}>
                <Button
                  component="a"
                  href="https://accounting-ai.github.io/rubidium-data-publications/pdfs/valuechains-methodology-paper-v-1-0/valuechains-methodology-paper-v-1-0.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  color="inherit"
                  aria-label="Methodology Working Paper"
                  sx={{
                    minWidth: 260,
                    bgcolor: 'rgba(255,255,255,0.16)',
                    color: 'common.white',
                    border: '1px solid rgba(255,255,255,0.55)',
                    backdropFilter: 'saturate(160%) blur(4px)',
                    boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.24)',
                      borderColor: 'rgba(255,255,255,0.7)',
                      boxShadow: '0 8px 22px rgba(0,0,0,0.18)'
                    }
                  }}
                >
                  Methodology Working Paper
                </Button>
                <Button
                  component="a"
                  href="https://accounting-ai.github.io/rubidium-data-publications/pdfs/make-what-you-buy/make-what-you-buy.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  color="inherit"
                  aria-label="Make What You Buy Working Paper"
                  sx={{
                    minWidth: 260,
                    bgcolor: 'rgba(255,255,255,0.16)',
                    color: 'common.white',
                    border: '1px solid rgba(255,255,255,0.55)',
                    backdropFilter: 'saturate(160%) blur(4px)',
                    boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.24)',
                      borderColor: 'rgba(255,255,255,0.7)',
                      boxShadow: '0 8px 22px rgba(0,0,0,0.18)'
                    }
                  }}
                >
                  Procurement Working Paper
                </Button>
              </Box>
            </Box>
          </Grid>
          {/* Desktop/Tablet right column: full paragraph with links */}
          <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Typography variant="body1" sx={{ textAlign: 'justify', color: 'inherit', m: 0 }}>
              The Productive Capabilities Data Lab is an applied AI research and development hub specializing in analytics and datasets for industrial strategy: It is a non-profit, public-purpose collaboration between{' '}
              <Link href="https://www.i3-t.org/" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: 'common.white', fontWeight: 700, '&:hover': { color: 'common.white', textDecoration: 'underline' } }}>i3T</Link>{' '}and{' '}
              <Link href="https://rubidiumdata.com/" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: 'common.white', fontWeight: 700, '&:hover': { color: 'common.white', textDecoration: 'underline' } }}>RubidiumData</Link>.
            </Typography>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default HeroBanner;
